import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ImageBackground, TextInput, Pressable, ScrollView, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const fontFamilies = {
    Bodoni72Smallcaps: "Bodoni72Smallcaps",
};

const Countdown = ({ 
    targetDate, 
    onCompleteMessage, 
    backgroundImage, 
    applyBlur, 
    title, 
    onTitleChange, 
    onDateChange, 
    onMessageChange,
    timelineEventId, // kept for backward compatibility
    eventId,
    onTimelineLink,
    timelineEvents,
    isOwner,
    isEditing,
    onRefreshTimeline
}) => {
    const effectiveEventId = eventId || timelineEventId;

    const [timeLeft, setTimeLeft] = useState(null);
    const [editableTitle, setEditableTitle] = useState(title || "Countdown");
    const [editableDate, setEditableDate] = useState(targetDate);
    const [editableMessage, setEditableMessage] = useState(onCompleteMessage || "Event has started!");
    const [showTimelineSelector, setShowTimelineSelector] = useState(false);
    const [linkedTimelineEvent, setLinkedTimelineEvent] = useState(null);

    // Load Timeline event if linked
    useEffect(() => {
        if (effectiveEventId && timelineEvents) {
            const event = timelineEvents.find(e => e.id === effectiveEventId);
            if (event) {
                setLinkedTimelineEvent(event);
                setEditableTitle(event.title);
                setEditableDate(event.datetime);
                
            } else {
                
            }
        } else {
            setLinkedTimelineEvent(null);
        }
    }, [effectiveEventId, timelineEvents]);

    useEffect(() => {
        setEditableTitle(title || "Countdown");
        setEditableDate(targetDate);
        setEditableMessage(onCompleteMessage || "Event has started!");
    }, [title, targetDate, onCompleteMessage]);

    useEffect(() => {
        const calculateTimeLeft = () => {
            const targetTime = new Date(targetDate).getTime();
            const now = new Date().getTime();

            if (isNaN(targetTime) || targetTime <= now) {
                setTimeLeft(null);
                return;
            }

            const difference = targetTime - now;

            if (difference > 0) {
                setTimeLeft({
                    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                    minutes: Math.floor((difference / 1000 / 60) % 60),
                    seconds: Math.floor((difference / 1000) % 60)
                });
            } else {
                setTimeLeft(null);
            }
        };

        calculateTimeLeft();
        const timer = setInterval(calculateTimeLeft, 1000);

        return () => clearInterval(timer);
    }, [targetDate]);

    const handleTitleChange = (newTitle) => {
        if (!linkedTimelineEvent) {
            setEditableTitle(newTitle);
            if (onTitleChange) onTitleChange(newTitle);
        }
    };

    const handleDateChange = (newDate) => {
        if (!linkedTimelineEvent) {
            setEditableDate(newDate);
            if (onDateChange) onDateChange(newDate);
        }
    };

    const handleMessageChange = (newMessage) => {
        setEditableMessage(newMessage);
        if (onMessageChange) onMessageChange(newMessage);
    };

    const handleTimelineLink = (eventId) => {
        if (onTimelineLink) {
            onTimelineLink(eventId);
        }
        setShowTimelineSelector(false);
    };

    const handleUnlink = () => {
        setLinkedTimelineEvent(null);
        if (onTimelineLink) {
            onTimelineLink(null);
        }
    };

    return (
        <View style={styles.container}>
            <ImageBackground
                source={backgroundImage || require("../../assets/Hub Background.png")}
                style={styles.backgroundImage}
                blurRadius={applyBlur ? 10 : 0}
            >
                <View style={styles.contentContainer}>

                    <Modal
                        visible={showTimelineSelector}
                        transparent={true}
                        animationType="fade"
                        onRequestClose={() => setShowTimelineSelector(false)}
                    >
                        <Pressable 
                            style={styles.modalOverlay}
                            onPress={() => setShowTimelineSelector(false)}
                        >
                            <Pressable 
                                style={styles.modalContent}
                                onPress={(e) => e.stopPropagation()}
                            >
                                <View style={styles.modalHeader}>
                                    <Text style={styles.modalTitle}>Select Timeline Event</Text>
                                    <View style={{ flexDirection: 'row', gap: 10 }}>
                                        {onRefreshTimeline && (
                                            <Pressable onPress={onRefreshTimeline} style={styles.refreshButton}>
                                                <Ionicons name="refresh" size={22} color="#4c956c" />
                                            </Pressable>
                                        )}
                                        <Pressable onPress={() => setShowTimelineSelector(false)} style={styles.closeButton}>
                                            <Ionicons name="close" size={24} color="#666" />
                                        </Pressable>
                                    </View>
                                </View>

                                {timelineEvents && timelineEvents.length > 0 ? (
                                    <ScrollView style={styles.eventsList} showsVerticalScrollIndicator={true}>
                                        {timelineEvents.map(event => (
                                            <Pressable 
                                                key={event.id}
                                                onPress={() => handleTimelineLink(event.id)}
                                                style={styles.eventOption}
                                            >
                                                <View style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 6 }}>
                                                    <Ionicons name="calendar" size={18} color="#4c956c" style={{ marginRight: 10, marginTop: 2 }} />
                                                    <Text style={styles.eventOptionText}>{event.title}</Text>
                                                </View>
                                                <Text style={styles.eventOptionDate}>
                                                    {new Date(event.datetime).toLocaleDateString('en-US', { 
                                                        month: 'short', 
                                                        day: 'numeric', 
                                                        year: 'numeric',
                                                        hour: 'numeric',
                                                        minute: '2-digit'
                                                    })}
                                                </Text>
                                            </Pressable>
                                        ))}
                                    </ScrollView>
                                ) : (
                                    <View style={styles.emptyState}>
                                        <Ionicons name="calendar-outline" size={48} color="#ccc" />
                                        <Text style={styles.emptyTitle}>No Timeline Events</Text>
                                        <Text style={styles.emptyText}>Create events in the Timeline widget first</Text>
                                    </View>
                                )}
                            </Pressable>
                        </Pressable>
                    </Modal>

                    <TextInput
                        style={[styles.title, linkedTimelineEvent && { backgroundColor: 'rgba(255, 215, 0, 0.2)', borderRadius: 8, paddingHorizontal: 15 }]}
                        value={editableTitle}
                        onChangeText={handleTitleChange}
                        placeholder={linkedTimelineEvent ? linkedTimelineEvent.title : "Enter countdown title"}
                        placeholderTextColor="#fefde2"
                        editable={false}
                    />

                    {timeLeft ? (
                        <View style={styles.timeContainer}>
                            <View style={styles.timeBlock}>
                                <Text style={styles.number}>{timeLeft.days}</Text>
                                <Text style={styles.label}>Days</Text>
                            </View>
                            <View style={styles.timeBlock}>
                                <Text style={styles.number}>{timeLeft.hours}</Text>
                                <Text style={styles.label}>Hours</Text>
                            </View>
                            <View style={styles.timeBlock}>
                                <Text style={styles.number}>{timeLeft.minutes}</Text>
                                <Text style={styles.label}>Minutes</Text>
                            </View>
                            <View style={styles.timeBlock}>
                                <Text style={styles.number}>{timeLeft.seconds}</Text>
                                <Text style={styles.label}>Seconds</Text>
                            </View>
                        </View>
                    ) : (
                        <Text style={styles.completeMessage}>{editableMessage}</Text>
                    )}
                </View>
            </ImageBackground>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        width: '100%',
        minHeight: 200,
        borderRadius: 15,
        overflow: 'hidden',
        marginVertical: 20,
    },
    backgroundImage: {
        width: '100%',
        height: '100%',
    },
    contentContainer: {
        padding: 20,
        backgroundColor: 'rgba(76, 149, 108, 0.85)',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    editControls: {
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 10,
    },
    linkButton: {
        backgroundColor: 'rgba(254, 253, 226, 0.9)',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 15,
        flexDirection: 'row',
        alignItems: 'center',
    },
    linkButtonText: {
        color: '#4c956c',
        fontSize: 14,
        fontWeight: '600',
    },
    linkedBadge: {
        flexDirection: 'row',
        backgroundColor: 'rgba(255, 215, 0, 0.9)',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 15,
        alignItems: 'center',
    },
    unlinkButton: {
        backgroundColor: '#d9534f',
        borderRadius: 10,
        marginLeft: 6,
        padding: 2,
    },
    linkedText: {
        color: '#4c956c',
        fontSize: 14,
        fontWeight: '600',
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContent: {
        backgroundColor: '#fff',
        borderRadius: 16,
        width: '100%',
        maxWidth: 500,
        maxHeight: '80%',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.3,
        shadowRadius: 12,
        elevation: 10,
    },
    modalHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 20,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    modalTitle: {
        fontSize: 22,
        fontWeight: '700',
        color: '#4c956c',
    },
    refreshButton: {
        padding: 4,
    },
    closeButton: {
        padding: 4,
    },
    eventsList: {
        maxHeight: 400,
        padding: 15,
    },
    eventOption: {
        padding: 16,
        borderRadius: 12,
        marginBottom: 10,
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#e0e0e0',
    },
    eventOptionText: {
        fontSize: 18,
        color: '#333',
        fontWeight: '600',
        flex: 1,
        flexShrink: 1,
        lineHeight: 24,
    },
    eventOptionDate: {
        fontSize: 16,
        color: '#666',
        marginTop: 6,
        fontWeight: '500',
        marginLeft: 28,
    },
    emptyState: {
        padding: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    emptyTitle: {
        fontSize: 20,
        fontWeight: '600',
        color: '#333',
        marginTop: 16,
        marginBottom: 8,
    },
    emptyText: {
        fontSize: 16,
        color: '#999',
        textAlign: 'center',
    },
    title: {
        fontFamily: fontFamilies.Bodoni72Smallcaps,
        fontSize: 36,
        color: '#fefde2',
        marginBottom: 20,
        textAlign: 'center',
        minWidth: 200,
        padding: 10,
        textShadowOffset: { width: 2, height: 2 },
        textShadowRadius: 4,
        textShadowColor: 'rgba(0, 0, 0, 0.3)',
    },
    timeContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 20,
    },
    timeBlock: {
        backgroundColor: 'rgba(254, 253, 226, 0.9)',
        borderRadius: 10,
        padding: 15,
        minWidth: 100,
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
        elevation: 5,
    },
    number: {
        fontFamily: fontFamilies.Bodoni72Smallcaps,
        fontSize: 40,
        color: '#4c956c',
        fontWeight: 'bold',
    },
    label: {
        fontFamily: fontFamilies.Bodoni72Smallcaps,
        fontSize: 18,
        color: '#4c956c',
        marginTop: 5,
    },
    completeMessage: {
        fontFamily: fontFamilies.Bodoni72Smallcaps,
        fontSize: 28,
        color: '#fefde2',
        textAlign: 'center',
        textShadowOffset: { width: 2, height: 2 },
        textShadowRadius: 4,
        textShadowColor: 'rgba(0, 0, 0, 0.3)',
    },
});

export default Countdown;